import React, { useMemo, useState } from "react";
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalCloseButton,
  Tabs, TabList, TabPanels, Tab, TabPanel,
  Box, Button, HStack, Text, Alert, AlertIcon, Spinner, SimpleGrid,
} from "@chakra-ui/react";
import MetricCard from "../../components/analytics/MetricCard";
import { useLiveActivity } from "./liveActivity/useLiveActivity";
import MemoryChart, { DARK } from "./liveActivity/MemoryChart";
import { RangeMenu, EventsFilter, EVENT_TYPES } from "./liveActivity/MemoryControls";
import { chartEvents, clampWindow } from "./liveActivity/memoryChartData";
import SpikeList, { buildEvents } from "./liveActivity/SpikeList";
import LiveUsersTable from "./liveActivity/LiveUsersTable";
import ContributorsPanel from "./liveActivity/ContributorsPanel";
import HeavyRequestsTable from "./liveActivity/HeavyRequestsTable";
import { formatMb, formatBytes, formatAgo } from "./liveActivity/format";
import MemoryIssuesPanel from "./liveActivity/MemoryIssuesPanel";
import DbHealthPanel from "./liveActivity/DbHealthPanel";
import CodeReviewPanel from "./liveActivity/CodeReviewPanel";
import AlertsPanel from "./liveActivity/AlertsPanel";
import {
  fetchMemoryIssues, verifyMemoryIssue as verifyMemoryIssueService, runDbHealthCheck as runDbHealthCheckService,
  runCodeReview as runCodeReviewService, fetchNightlyCodeReviews, fetchCodeFindings, dismissCodeFinding,
  fetchLatestNightlyCheck, runMemoryScan as runMemoryScanService, startAiReview as startAiReviewService,
  fetchAiReviewStatus, fetchAlerts, saveAlertRecipient, removeAlertRecipient, sendTestAlert, startSizingFindings,
  fetchSizingStatus,
} from "../../services/liveActivity";

// A custom range ending this close to now (the inputs only hold whole minutes) runs to now.
const NOW_SLACK_MS = 2 * 60 * 1000;

export default function LiveActivityModal({
  isOpen, onClose, fetcher,
  loadMemoryIssues = fetchMemoryIssues,
  verifyMemoryIssue = verifyMemoryIssueService,
  runDbHealthCheck = runDbHealthCheckService,
  runCodeReview = runCodeReviewService,
  loadNightlyReviews = fetchNightlyCodeReviews,
  // Shared by the three issue tabs: the nightly AI review's findings (filed by area) and last
  // night's automatic re-run of each check.
  loadFindings = fetchCodeFindings,
  dismissFinding = dismissCodeFinding,
  loadLastNight = fetchLatestNightlyCheck,
  runMemoryScan = runMemoryScanService,
  startAiReview = startAiReviewService,
  loadAiReviewStatus = fetchAiReviewStatus,
  // Each issue tab's manual run also sizes the AI findings saved without an impact.
  startSizing = startSizingFindings,
  loadSizing = fetchSizingStatus,
  // The Alerts tab: who gets the morning summary and the urgent alerts.
  loadAlerts = fetchAlerts,
  saveRecipient = saveAlertRecipient,
  removeRecipient = removeAlertRecipient,
  sendTestEmail = sendTestAlert,
}) {
  const {
    data, loading, error, at, setAt, windowSec, setWindowSec, live, refresh, lastUpdated,
  } = useLiveActivity({ enabled: isOpen, fetcher });

  const [selectedEvent, setSelectedEvent] = useState(null);
  const [eventTypes, setEventTypes] = useState(EVENT_TYPES.map((t) => t.value));
  // Opening an event shows its details; the feed stays live (the details say who and what).
  const selectEvent = (iso) => setSelectedEvent(iso);
  // The feed is always live, except for a custom range that ends before now.
  const applyCustomRange = (fromIso, toIso) => {
    const now = Date.now();
    const from = Date.parse(fromIso);
    const to = Date.parse(toIso);
    if (to >= now - NOW_SLACK_MS) {
      setWindowSec(clampWindow((now - from) / 1000));
      setAt(null);
    } else {
      setWindowSec(clampWindow((to - from) / 1000));
      setAt(toIso);
    }
  };
  const latest = data?.server?.latest || null;
  const users = data?.users || [];
  const online = users.filter((u) => u.state === "online");
  const activeNow = online.filter((u) => u.active);
  const limit = data?.server?.limit_mb ?? latest?.cgroup_limit_mb ?? null;
  // With a limit, the container's memory is what counts (it's what gets the server killed).
  // Without one (a local machine), "container" means the whole machine, so use the process.
  const used = (limit ? (latest?.cgroup_used_mb ?? latest?.rss_mb) : (latest?.rss_mb ?? latest?.cgroup_used_mb)) ?? null;
  const pct = limit && used != null ? Math.round((used / limit) * 100) : null;
  const memory = data?.server?.memory || null;
  // The chart shows the server answering, so its events need no name, unless the backend couldn't
  // tell which server it is and shows every server writing to the database.
  const serverLabels = useMemo(() => ((memory?.groups || []).length > 1
    ? new Map(memory.groups.map((g) => [g.key, g.label])) : null), [memory]);
  const markers = useMemo(() => chartEvents({
    detailed: buildEvents(data?.spikes, data?.restarts, limit),
    series: memory?.events || [],
    show: { restarts: eventTypes.includes("restarts"), jumps: eventTypes.includes("jumps") },
    bucketSec: memory?.bucket_s || 60,
  }), [data, limit, memory, eventTypes]);

  return (
    // No page scroll lock: Chakra's lock on the first open modal swallows wheel
    // events in any modal stacked on top of it — the Assign window and the
    // Messages modal both open over this one from the Memory Issues tab.
    <Modal isOpen={isOpen} onClose={onClose} size="6xl" scrollBehavior="inside" blockScrollOnMount={false}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Live Activity</ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          <Tabs colorScheme="purple" isLazy>
            <TabList>
              <Tab>Live Activity</Tab>
              <Tab>Memory Issues</Tab>
              <Tab>DB Health Check</Tab>
              <Tab>Code Review</Tab>
              <Tab>Alerts</Tab>
            </TabList>
            <TabPanels>
              <TabPanel px={0}>
                <Box bg={DARK.panel} color={DARK.text} borderWidth={1} borderColor={DARK.border} borderRadius="md"
                     p={4} mb={4} data-testid="memory-panel">
                  <HStack justify="space-between" spacing={3} flexWrap="wrap" mb={4} align="flex-start">
                    <HStack spacing={2} flexWrap="wrap" align="flex-start">
                      <EventsFilter value={eventTypes} onChange={setEventTypes} />
                      <RangeMenu
                        windowSec={windowSec}
                        at={at}
                        onPreset={(sec) => { setWindowSec(sec); setAt(null); }}
                        onCustom={applyCustomRange}
                      />
                    </HStack>
                    <HStack spacing={3}>
                      {live ? (
                        <HStack spacing={1.5} data-testid="live-indicator" title="Refreshes on its own">
                          <Box w="8px" h="8px" borderRadius="full" bg="#22c55e" />
                          <Text fontSize="sm" color={DARK.muted}>Live</Text>
                        </HStack>
                      ) : (
                        <Button size="sm" variant="outline" color={DARK.text} borderColor={DARK.border}
                                _hover={{ bg: DARK.hover }} onClick={() => setAt(null)} data-testid="back-to-live">
                          Back to live
                        </Button>
                      )}
                      <Button size="sm" variant="outline" color={DARK.text} borderColor={DARK.border}
                              _hover={{ bg: DARK.hover }} onClick={refresh} isLoading={loading && !!data}>
                        Refresh
                      </Button>
                      {lastUpdated && <Text fontSize="xs" color={DARK.muted}>updated {formatAgo(lastUpdated)}</Text>}
                    </HStack>
                  </HStack>
                  {!data && loading && <Spinner color={DARK.muted} />}
                  {data && (
                    <MemoryChart
                      memory={memory}
                      windowStart={data.window_start}
                      windowEnd={data.window_end}
                      events={markers}
                      onSelectEvent={selectEvent}
                    />
                  )}
                </Box>

                {error && (
                  <Alert status="error" mb={3}><AlertIcon />{error}</Alert>
                )}
                {data?.requests_truncated && (
                  <Alert status="warning" mb={3}>
                    <AlertIcon />
                    Showing only the 500 most recent heavy requests — rankings and jump details use just these; pick a shorter window for the full picture.
                  </Alert>
                )}
                {data && (
                  <>
                    <SimpleGrid columns={{ base: 2, md: 3, lg: 6 }} spacing={3} mb={4}>
                      <MetricCard label="Online now" value={online.length} color="purple.500" helpText={`${users.length - online.length} seen in the last 10 min`} />
                      <MetricCard label="Active now" value={activeNow.length} helpText="typing or clicking in the last 30s" />
                      <MetricCard
                        label="Server memory"
                        value={formatMb(used)}
                        helpText={pct != null ? `${pct}% of ${formatMb(limit)} limit`
                          : (latest?.service === "local" ? "no limit on a computer" : "memory limit unknown here")}
                        color={pct != null && pct >= 85 ? "red.500" : "blue.500"}
                      />
                      <MetricCard label="Requests running" value={latest?.in_flight_requests ?? "—"} helpText="at the last sample" />
                      <MetricCard label="Documents open in memory" value={latest?.collab_rooms ?? "—"} helpText={latest ? formatBytes(latest.collab_snapshot_bytes) : undefined} />
                      <MetricCard label="AI document cache" value={latest ? formatBytes(latest.document_cache_chars) : "—"} helpText={latest ? `${latest.document_cache_entries} patients cached` : undefined} />
                    </SimpleGrid>

                    <SpikeList
                      spikes={data.spikes}
                      restarts={data.restarts}
                      requests={data.requests}
                      limitMb={limit}
                      selected={selectedEvent}
                      onSelect={selectEvent}
                      coveredSince={data.details_since}
                      serverLabels={serverLabels}
                    />

                    <Tabs colorScheme="purple" mt={4} isLazy>
                      <TabList>
                        <Tab>Who's online ({users.length})</Tab>
                        <Tab>What's using memory</Tab>
                        <Tab>Heavy requests ({data.requests.length})</Tab>
                      </TabList>
                      <TabPanels>
                        <TabPanel px={0}><LiveUsersTable users={users} /></TabPanel>
                        <TabPanel px={0}><ContributorsPanel contributors={data.contributors} /></TabPanel>
                        <TabPanel px={0}><HeavyRequestsTable requests={data.requests} /></TabPanel>
                      </TabPanels>
                    </Tabs>
                  </>
                )}
              </TabPanel>
              <TabPanel px={0}>
                <MemoryIssuesPanel load={loadMemoryIssues} verify={verifyMemoryIssue} scan={runMemoryScan}
                                   loadFindings={loadFindings} dismissFinding={dismissFinding}
                                   loadLastNight={loadLastNight} startSizing={startSizing} loadSizing={loadSizing} />
              </TabPanel>
              <TabPanel px={0}>
                <DbHealthPanel run={runDbHealthCheck} loadFindings={loadFindings} dismissFinding={dismissFinding}
                               loadLastNight={loadLastNight} startSizing={startSizing} loadSizing={loadSizing} />
              </TabPanel>
              <TabPanel px={0}>
                <CodeReviewPanel run={runCodeReview} loadNightly={loadNightlyReviews} loadFindings={loadFindings}
                                 dismissFinding={dismissFinding} loadLastNight={loadLastNight}
                                 startAi={startAiReview} loadAiStatus={loadAiReviewStatus}
                                 startSizing={startSizing} loadSizing={loadSizing} />
              </TabPanel>
              <TabPanel px={0}>
                <AlertsPanel load={loadAlerts} save={saveRecipient} remove={removeRecipient}
                             sendTest={sendTestEmail} />
              </TabPanel>
            </TabPanels>
          </Tabs>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
